/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { useLoading } from 'components/process/Loading';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CommonUtil } from 'utils/CommonUtil';
import { findModelSimilar, saveModelSimilar } from 'apis/md/MdModel';
import { MdModelSimilar } from 'models/md/MdModel';
import { PageMode } from 'models/common/Common';
import { hasRole } from 'utils/SessionUtil';

/**
 * @desc 투자비관리 > 기준정보 > 모델 유사도 관리
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.19   jshwang12       신규 생성
 */
const MdMdlSmlyMgtPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [code, setCode] = useState<any>();
  const [rowData, setRowData] = useState<MdModelSimilar[]>([]);
  const [pageMode, setPageMode] = useState<PageMode>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const getCommonCodes = async () => {
    const mdlSpecCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MDL_SPEC_CD',
      optValCtn5: 'Y',
    });
    const mdEvlTpCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MD_EVL_TP_CD',
      optValCtn1: 'LIST', // 유사도유형 목록에 해당하는 평가유형코드만 조회 (유사도유형 범위인 경우에는 key-in)
    }); // 평가유형코드 (ex. 일치/불일치/범위(절대값))
    const mdClfuDivsCd: Code[] = await getCommonCodeNames('MD_CLFU_DIVS_CD');
    const mdMdlSpecCtgr: Code[] = await getCommonCodeNames('MD_MDL_SPEC_CTGR');
    const specCtgr = (mdMdlSpecCtgr || []).reduce((acc, { cmnCd, cmnCdNm }) => {
      if (cmnCd) {
        acc[cmnCd] = cmnCdNm;
      }
      return acc;
    }, {});

    setCode({
      mdClfuDivsCd: mdClfuDivsCd,
      mdlSpecCd: mdlSpecCd,
      mdEvlTpCd: mdEvlTpCd,
      mdMdlSpecCtgr: mdMdlSpecCtgr,
      specCtgr: specCtgr, // 사양분류명 {cmnCd: cmnCdNm} 형태
    });
  };

  /**
   * 조회
   */
  const handleSearch = () => {
    findModelSimilar().then((result) => setRowData(result));
  };

  const validate = () => {
    const valid = (gridData || [])
      .map((o, index) => {
        if (_.isEmpty(o.clfuDivsCd)) {
          return `${index + 1} : ${t(
            'md.label.유사도 유형을 선택해 주세요.',
            '유사도 유형을 선택해 주세요.'
          )}`;
        }
        // 유사도 유형이 목록인 경우
        if (o.clfuDivsCd === 'LIST') {
          if (!o.evlTpCd0 || !o.evlTpCd5) {
            return `${index + 1} : ${t(
              'md.label.일치/불일치를 선택해 주세요.',
              '일치/불일치를 선택해 주세요.'
            )}`;
          }
        }
        // 유사도 유형이 범위인 경우
        else {
          let empty = false;
          for (let i = 1; i < 6; i++) {
            if (!empty) {
              empty = !o[`minDifNvl${i}`] || !o[`maxDifNvl${i}`];
            }
          }
          if (empty) {
            return `${index + 1} : ${t(
              'md.label.유사도 점수 From/To를 입력해 주세요.',
              '유사도 점수 From/To를 입력해 주세요.'
            )}`;
          }
        }
        if (_.isEmpty(o.wghtScor)) {
          return `${index + 1} : ${t(
            'md.label.가중치를 입력해 주세요.',
            '가중치를 입력해 주세요.'
          )}`;
        }
        if (_.isNaN(Number(o.wghtScor))) {
          return `${index + 1} : ${t(
            'md.label.가중치 값을 확인해 주세요.',
            '가중치 값을 확인해 주세요.'
          )}`;
        }
        if (_.isEmpty(String(o.prioRank))) {
          return `${index + 1} : ${t(
            'md.label.우선순위를 입력해 주세요.',
            '우선순위를 입력해 주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    const totalWghtScor = (gridData || []).reduce((acc, cur) => acc + Number(cur.wghtScor), 0);
    if (_.isNaN(totalWghtScor)) {
      openMessageBar({
        type: 'error',
        content: t('md.label.가중치 값을 확인해 주세요.', '가중치 값을 확인해 주세요.'),
      });
      return false;
    }
    if (Math.round((totalWghtScor + Number.EPSILON) * 100) / 100 !== 20) {
      openMessageBar({
        type: 'error',
        content: t(
          'md.label.가중치 총 합을 20으로 맞춰 주세요.',
          '가중치 총 합을 20으로 맞춰 주세요.'
        ),
      });
      return false;
    }
    return true;
  };

  /**
   * 저장
   */
  const handleSave = () => {
    if (!validate()) return;
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        saveModelSimilar(gridData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res.data && typeof res.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            handleSearch();
          }
        });
      },
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.모델 유사도 목록', '모델 유사도 목록'),
      t('md.label.모델 유사도 목록', '모델 유사도 목록')
    );
  };

  const gridData = useMemo(() => {
    return (code?.mdlSpecCd || []).map((o) => {
      const row = (rowData || []).filter((d) => d.specAtcId === o.cmnCd);
      const data = {};

      const item = {
        specAtcId: o.cmnCd,
        specAtcNm: o.cmnCdNm,
        ctgrCd: o.optValCtn1,
        ctgrNm: code?.specCtgr?.[o.optValCtn1] || o.optValCtn1 || o.cmnCdNm, // 카테고리 없는 경우 셀병합을 위해 동일한 값 사용
        ...(row.length > 0 ? row[0] : {}),
      };
      // LIST 타입인 경우 evlTpCd 값을 minDifNvl, maxDifNvl에 복사
      if (item.clfuDivsCd === 'LIST') {
        for (let i = 0; i < 6; i++) {
          const evlTpCd = item[`evlTpCd${i}`] || '';
          item[`minDifNvl${i}`] = evlTpCd;
          item[`maxDifNvl${i}`] = evlTpCd;
        }
      }
      return item;
    });
  }, [code, rowData]);

  const layoutDefinition = useMemo(() => {
    const mapMdEvlTpCd = new DataMap(code?.mdEvlTpCd || [], 'cmnCd', 'cmnCdNm');
    const scoring = Array.from({ length: 6 }, (_, i) => ({
      header: String(t(`md.label.${i}`, `${i}`)),
      columns: [
        {
          binding: `crtnScor${i}`,
          visible: false,
        },
        {
          binding: `minDifNvl${i}`,
          header: String(t('md.label.From', 'From')),
          align: 'center',
          width: 65,
          minWidth: 50,
          cellTemplate: (params) => {
            // 유사도유형이 목록인 경우 코드명 노출
            if (params.item.clfuDivsCd === 'LIST') {
              return mapMdEvlTpCd.getDisplayValue(params.value) || '';
            }
            return params.value || '';
          },
        },
        {
          binding: `maxDifNvl${i}`,
          header: String(t('md.label.To', 'To')),
          align: 'center',
          width: 65,
          minWidth: 50,
          cellTemplate: (params) => {
            // 유사도유형이 목록인 경우 코드명 노출
            if (params.item.clfuDivsCd === 'LIST') {
              return mapMdEvlTpCd.getDisplayValue(params.value) || '';
            }
            return params.value || '';
          },
        },
      ],
    }));
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 40,
        minWidth: 40,
        align: 'center',
        isReadOnly: true,
        cssClass: 'text-center transposed-header',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        header: String(t('com.label.구분', '구분')),
        columns: [
          {
            binding: 'ctgrNm',
            header: String(t('md.label.사양항목', '사양항목')),
            width: 80,
            minWidth: 50,
            align: 'center',
            allowMerging: true,
            isReadOnly: true,
            cssClass: 'text-center transposed-header',
          },
          {
            binding: 'specAtcNm',
            header: String(t('md.label.사양항목', '사양항목')),
            width: 80,
            minWidth: 50,
            align: 'center',
            allowMerging: true,
            isReadOnly: true,
            cssClass: 'text-center transposed-header',
          },
          {
            binding: 'clfuDivsCd',
            header: String(t('md.label.유사도\n유형', '유사도\n유형')),
            align: 'center',
            width: 60,
            minWidth: 50,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.mdClfuDivsCd || [], 'cmnCd', 'cmnCdNm'),
          },
        ],
      },
      {
        binding: 'wghtScor',
        header: String(t('md.label.가중치', '가중치')),
        align: 'center',
        width: 80,
        minWidth: 50,
      },
      {
        binding: 'prioRank',
        header: String(t('md.label.우선순위', '우선순위')),
        align: 'center',
        width: 80,
        minWidth: 50,
      },
      {
        header: String(t('md.label.Scoring', 'Scoring')),
        columns: [...scoring],
      },
      {
        binding: 'rmk',
        header: String(t('md.label.비고', '비고')),
        align: 'left',
        width: '*',
        minWidth: 80,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        width: 100,
        minWidth: 80,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일', '수정일')),
        align: 'center',
        width: 100,
        minWidth: 80,
        isReadOnly: true,
      },
      {
        binding: 'specAtcId',
        visible: false,
      },
    ];
  }, [code]);

  const getScore = (binding = '') => {
    return Number(binding.match(/(\d+)$/)?.[1] || -1);
  };

  const showEvalKeyIn = (clfuDivsCd = '', binding = '') => {
    if (clfuDivsCd !== 'RANGE') return false;
    if (!binding.includes('DifNvl')) return false;

    // 유사도 유형이 Range인 경우 1 ~ 5 min/max 입력
    const score = getScore(binding);
    return score > 0;
  };

  const showEvalCombobox = (clfuDivsCd = '', binding = '') => {
    if (clfuDivsCd !== 'LIST') return false;
    if (!binding.includes('DifNvl')) return false;

    const arr = binding.match(/^(min|max)DifNvl(\d+)$/) || [];
    const fromTo = arr.length > 0 ? arr[1] : '';
    const score = arr.length > 1 ? arr[2] : '';
    // 유사도 유형이 List인 경우 콤보박스 (0 From, 5 To만 입력가능)
    return (fromTo === 'min' && score === '0') || (fromTo === 'max' && score === '5');
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (!hasAuth || PageMode.EDIT !== pageMode) {
        cell.ariaReadOnly = true;
      } else {
        // 평가값 (From, To)
        if (binding?.includes('DifNvl')) {
          const isCombo = showEvalCombobox(item.clfuDivsCd, binding);
          if (item.clfuDivsCd === 'LIST') {
            // 유사도 유형이 List인 경우 콤보박스 (0 From, 5 To만 입력가능)
            cell.ariaReadOnly = !isCombo;
          } else {
            const isKeyIn = showEvalKeyIn(item.clfuDivsCd, binding);
            // 유사도 유형이 Range인 경우 1 ~ 5 From/To 입력
            cell.ariaReadOnly = !isKeyIn;
          }
          toggleClass(cell, 'WijmoSelect', isCombo);
          if (!isCombo && cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (!hasAuth || PageMode.EDIT !== pageMode) {
      e.cancel = true;
    } else {
      // 유사도 유형이 LIST인 경우 콤보박스, Range인 경우 keyin
      // 평가값 (From, To)
      if (binding?.includes('DifNvl')) {
        if (item.clfuDivsCd === 'LIST') {
          const isCombo = showEvalCombobox(item.clfuDivsCd, binding);
          // 유사도 유형이 List인 경우 콤보박스 (0 From, 5 To만 입력가능)
          if (isCombo) {
            col.dataMap = new DataMap(code?.mdEvlTpCd || [], 'cmnCd', 'cmnCdNm');
          } else {
            e.cancel = true;
          }
        } else {
          // 유사도 유형이 Range인 경우 1 ~ 5 From/To 입력
          const isKeyIn = showEvalKeyIn(item.clfuDivsCd, binding);
          col.dataMap = null;
          if (!isKeyIn) {
            e.cancel = true;
          }
        }
      }
      if ('clfuDivsCd' === binding) {
        // 유사도 유형
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    // 유사도 유형
    if ('clfuDivsCd' === binding) {
      const cd = (code?.mdClfuDivsCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      // 값이 변한 경우 초기화
      if (oldVal !== cd) {
        for (let i = 0; i < 6; i++) {
          item[`minDifNvl${i}`] = '';
          item[`maxDifNvl${i}`] = '';
        }
      }
    }
    // 가중치
    if ('wghtScor' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9.]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
        const value = parseFloat(newVal);
        if (!_.isNaN(value)) {
          setTimeout(() => {
            grid.setCellData(e.row, e.col, CommonUtil.roundNumber(value, 2));
          }, 0);
        }
      }
    }
    // 우선순위
    if ('prioRank' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
    // 점수
    if (binding?.includes('DifNvl')) {
      if (item.clfuDivsCd === 'LIST') {
        const cd = (code?.mdEvlTpCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
        if (!cd) {
          e.cancel = true;
          return;
        }
        // min, max값 동기화시키기 (화면에 노출하기 위함)
        const key = binding
          .replace('min', 'MAX_TMP')
          .replace('max', 'min')
          .replace('MAX_TMP', 'max');
        item[key] = cd;
      } else {
        // 범위인 경우 숫자만 입력 가능
        if (newVal) {
          if (!/^-?[0-9.]+$/.test(newVal)) {
            e.cancel = true;
            return;
          }
          const value = parseFloat(newVal);
          if (!_.isNaN(value)) {
            setTimeout(() => {
              grid.setCellData(e.row, e.col, CommonUtil.roundNumber(value, 2));
            }, 0);
          }
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = useEvent((grid) => {
    grid.autoRowHeights = true;
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.columnHeaders) {
        e.cell.style.display = 'flex';
        e.cell.style.alignItems = 'center';
        e.cell.style.justifyContent = 'center';
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
    });
  });

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.모델 유사도 목록', '모델 유사도 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(gridData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`mdModelSmlyGrid`}
        layoutDefinition={layoutDefinition}
        rowData={gridData}
        height={569}
        frozenColumns={4}
        isSelector={false}
        isFilter={false}
        allowPinning={false}
        allowSorting={false}
        isReadOnly={!hasAuth || PageMode.EDIT !== pageMode}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          {PageMode.EDIT !== pageMode ? (
            <Button
              css={IconButton.button}
              className="edit"
              onClick={() => setPageMode(PageMode.EDIT)}
              disableRipple
            >
              {t('com.button.수정', '수정')}
            </Button>
          ) : (
            <>
              <Button
                css={IconButton.button}
                className="cancel"
                onClick={() => {
                  setPageMode(PageMode.READ);
                  handleSearch();
                }}
                disableRipple
              >
                {t('com.button.취소', '취소')}
              </Button>
              <Button css={IconButton.button} className="save" onClick={handleSave} disableRipple>
                {t('com.button.저장', '저장')}
              </Button>
            </>
          )}
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default MdMdlSmlyMgtPage;
